// Internal imports
import XlinkAPI from '~/app/api/xlinkAPI.js';
import { NIST_LOG_EVENTS } from './clientNISTConstants';

const NIST_STORAGE_KEY = 'nistLogBuffer'; // sessionStorage key for log buffer
const NIST_RETRY_KEY = 'nistLogRetryQueue'; // sessionStorage key for retry queue

const FLUSH_INTERVAL = 10 * 60 * 1000; // 10 minutes
const MAX_ENTRIES = 500; // Max number of buffered log entries
const MAX_RETRIES = 5; // Max retry attempts for failed sends

// Load the initial buffer from sessionStorage
let logBuffer = JSON.parse(sessionStorage.getItem(NIST_STORAGE_KEY) || '[]');

// --- Helper Functions ---

// Save the current log buffer to sessionStorage
const saveBuffer = () => {
  sessionStorage.setItem(NIST_STORAGE_KEY, JSON.stringify(logBuffer));
};

// Save the retry queue to sessionStorage
const saveRetryQueue = queue => {
  sessionStorage.setItem(NIST_RETRY_KEY, JSON.stringify(queue));
};

// Load the retry queue from sessionStorage
const loadRetryQueue = () => {
  return JSON.parse(sessionStorage.getItem(NIST_RETRY_KEY) || '[]');
};

// Limit the buffer to MAX_ENTRIES, trimming older entries if needed
const limitBufferSize = () => {
  if (logBuffer.length > MAX_ENTRIES) {
    logBuffer = logBuffer.slice(-MAX_ENTRIES); // Keep only the last N entries
  }
  saveBuffer(); // Persist changes
};

// --- Public Logging Interface ---

export const NISTAuditLog = (
  actionKey,
  desc,
  title,
  statusCode = 0,
  errorCodeType = '',
  isHTTPResponse = false,
) => {
  // USER_LOGIN, RETURN_DELETED, etc. — check out NIST_LOG_EVENTS for all possible logs. Anything that does not exist, we have default values cover for that
  const log = NIST_LOG_EVENTS[actionKey];

  if (desc === '') {
    desc = log?.desc;
  }

  if (title === '') {
    title = log?.title;
  }

  addNISTLog(title, desc, statusCode, errorCodeType, isHTTPResponse);
};

// Add the log to the buffer to then batch out
export const addNISTLog = (
  eventType,
  eventActionDesc,
  statusCode,
  errorCodeType,
  isHTTPResponse,
) => {
  // We ONLY handle sending the client-side info — on the API we will use the payload (IF IT EXISTS) and retrieve the account and userID.
  const log = {
    eventType,
    eventActionDesc,
    statusCode,
    errorCodeType,
    isHTTPResponse,
    timestamp: new Date().toISOString(), // We need to set the timestamp on the client side for each individual log because we are batching
  };

  logBuffer.push(log);
  limitBufferSize(); // Ensure buffer doesn’t exceed limits
};

// --- Flush Logic ---

// Attempts to POST buffered logs to the server.
// On failure, saves the payload to retry queue.
const flushLogs = async () => {
  if (logBuffer.length === 0) return;

  const payload = [...logBuffer]; // Shallow copy to avoid mutation during async
  try {
    await XlinkAPI.postNISTLogs(payload);
    logBuffer = []; // Clear buffer after successful send
    saveBuffer();
  } catch (err) {
    enqueueRetry(payload); // Store for retry if request failed
  }
};

// --- Retry Logic ---

// Push a failed log payload into the retry queue
const enqueueRetry = logs => {
  const retryQueue = loadRetryQueue();

  retryQueue.push({
    logs,
    retryCount: 1,
    nextAttempt: Date.now() + 1000, // Initial retry after 1 second
  });

  saveRetryQueue(retryQueue);
};

// Attempts to resend failed log batches with exponential backoff.
// Drops entries that exceed MAX_RETRIES.
const flushRetryQueue = async () => {
  const queue = loadRetryQueue();
  const now = Date.now();
  const remaining = [];

  for (const item of queue) {
    if (item.retryCount > MAX_RETRIES) continue; // Drop if retries exceeded

    if (now >= item.nextAttempt) {
      try {
        // Call the API to post logs
        await XlinkAPI.postNISTLogs(item.logs);
        continue; // Success — do not re-add to remaining
      } catch (err) {
        // Increment retry attempt and apply exponential backoff
        item.retryCount += 1;
        item.nextAttempt = now + Math.pow(2, item.retryCount) * 1000;
      }
    }

    remaining.push(item); // Keep in retry queue
  }

  saveRetryQueue(remaining); // Update queue in storage
};

// --- Auto Flush Interval ---

// Flush logs and retry queue on a recurring interval
setInterval(() => {
  flushLogs();
  flushRetryQueue();
}, FLUSH_INTERVAL);

// Flush logs before the user navigates away or closes the tab
window.addEventListener('beforeunload', () => {
  flushLogs();
});
